import { ReactNode } from 'react';
import { FormattedMessage, FormattedPlural } from 'react-intl';
import styled from 'styled-components';
import tw from 'tailwind.macro';
import { formatDuration, getRoundPrice } from '../../../helpers/results';
import { ReactComponent as DoodleIcon } from '../../../images/illustrated/doodle.svg';
import { ReactComponent as HearthIcon } from '../../../images/illustrated/heart-72.svg';
import { ReactComponent as SmileIcon } from '../../../images/illustrated/smile-48.svg';
import { ReactComponent as WowIcon } from '../../../images/illustrated/wow-72.svg';
import { screens } from '../../../styles/constants';
import { DynamicSearchPageInformation } from '../types';
import messages from './messages';

const Doodle = styled(DoodleIcon)`
  ${tw`absolute`}
  transform: rotateY(180deg);
  left: -50px;
  top: -21px;
`;

const Wow = styled(WowIcon)`
  ${tw`absolute`}
  bottom: 0;
`;

const Heart = styled(HearthIcon)`
  ${tw`absolute`}
  right: -58px;
  top: -20px;
`;

const Smile = styled(SmileIcon)`
  ${tw`m-auto block md:absolute`}
  top: 0;
  left: -73px;
`;

const RelativeSpan = styled.span`
  ${tw`relative`}
`;

const Container = styled.div`
  ${tw`p-big rounded-30 bg-beige-500 flex flex-col`}
  gap:var(--size-huge2);

  @media (min-width: ${screens.md}) {
    padding: calc(2 * var(--size-medium2))
      calc(var(--size-medium2) + var(--size-huge2));
  }
`;

const Row = styled.div`
  ${tw`flex flex-col justify-center items-center flex-wrap lg:flex-row`}
  gap:var(--size-huge2);
`;

const generateHash = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return hash;
};

const generateNumberFromSeed = (seed, min, max) => {
  const random = (Math.sin(seed) + 1) / 2;
  return Math.floor(random * (max - min + 1)) + min;
};

type TripInformationProps = {
  distanceAsCrowFlies: number;
  information: DynamicSearchPageInformation;
};

const TripInformation = (props: TripInformationProps) => {
  const { distanceAsCrowFlies, information } = props;
  const seed = generateHash(window.location.href);
  const randomSalesAmount = generateNumberFromSeed(seed, 100, 10000);
  const kilometersTraveled = Math.floor(
    distanceAsCrowFlies * randomSalesAmount,
  );
  const shoulDisplayMillionsKm = kilometersTraveled > 10000;
  const displayedKilometersTraveled = shoulDisplayMillionsKm
    ? (kilometersTraveled / 1000000).toFixed(2)
    : kilometersTraveled;

  return (
    <Container>
      <Row>
        <Information
          content={
            <RelativeSpan>
              <Smile height={70} width={70} />
              {Math.round(distanceAsCrowFlies)} km
            </RelativeSpan>
          }
          header={<FormattedMessage {...messages.mainTableHeaderDistance} />}
        />

        <Information
          content={
            <span>
              <RelativeSpan>
                <Doodle height={72} width={72} />
                {getRoundPrice(information.price.min)}€
              </RelativeSpan>{' '}
              - {getRoundPrice(information.price.max)}€
            </span>
          }
          header={
            <FormattedMessage
              {...messages[
                information.transportType
                  ? 'mainTableHeaderPriceRangeSpecific'
                  : 'mainTableHeaderPriceRange'
              ]}
              values={{
                transportType: information.transportType && (
                  <FormattedMessage {...messages[information.transportType]} />
                ),
              }}
            />
          }
        />
      </Row>

      <Row>
        <Information
          content={
            <RelativeSpan>
              {displayedKilometersTraveled} <Wow />
            </RelativeSpan>
          }
          header={
            <FormattedMessage
              {...messages[
                information.transportType
                  ? 'kilometersTraveledSpecific'
                  : 'kilometersTraveled'
              ]}
              values={{
                transportType: information.transportType && (
                  <FormattedMessage {...messages[information.transportType]} />
                ),
                unit: shoulDisplayMillionsKm ? (
                  <FormattedMessage {...messages.millionsKmUnit} />
                ) : (
                  'Km'
                ),
              }}
            />
          }
        />
        <Information
          content={
            information.minNbStops > 0 ? (
              <FormattedPlural
                one={
                  <FormattedMessage
                    {...messages.mainTableStop}
                    values={{ stopsNb: information.minNbStops }}
                  />
                }
                other={
                  <FormattedMessage
                    {...messages.mainTableStops}
                    values={{ stopsNb: information.minNbStops }}
                  />
                }
                value={information.minNbStops}
              />
            ) : (
              <FormattedMessage {...messages.mainTableDirect} />
            )
          }
          header={<FormattedMessage {...messages.mainTableHeaderTripType} />}
        />
        <Information
          content={
            <RelativeSpan>
              {formatDuration(information.duration.min)}
              <Heart />
            </RelativeSpan>
          }
          header={
            <FormattedMessage
              {...messages[
                information.transportType
                  ? 'mainTableHeaderDurationSpecific'
                  : 'mainTableHeaderDuration'
              ]}
              values={{
                transportType: information.transportType && (
                  <FormattedMessage {...messages[information.transportType]} />
                ),
              }}
            />
          }
        />
      </Row>
    </Container>
  );
};

const InformationContainer = styled.div`
  ${tw`font-kyrial flex flex-col text-center`}
`;

const InformationHeader = styled.span`
  ${tw`m-auto text-title-large-m leading-title-large-m md:text-title-large md:leading-title-large`}
  max-width:234px;
`;

const InformationContent = styled.span`
  ${tw`text-title-xxxxlarge-m leading-title-xxxxlarge-m md:text-title-xxxlarge md:leading-title-xxxlarge`}
`;

type InformationProps = {
  content: ReactNode;
  header: ReactNode;
};

const Information = (props: InformationProps) => {
  const { content, header } = props;

  return (
    <InformationContainer>
      <InformationContent>{content}</InformationContent>
      <InformationHeader>{header}</InformationHeader>
    </InformationContainer>
  );
};

export default TripInformation;
